import { getFormProps, useForm } from "@conform-to/react"
import { getZodConstraint, parseWithZod } from "@conform-to/zod"
import * as E from "@react-email/components"
import { data, redirect, type ActionFunctionArgs } from "react-router"
import { Link, useActionData } from "react-router"
import { HoneypotInputs } from "remix-utils/honeypot/react"
import { z } from "zod"

import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx"
import {
	Form,
	FormContext,
	FormControl,
	FormErrorList,
	FormField,
	FormLabel,
	FormMessage,
} from "#app/components/ui/forms/conform-integrated/form"
import { Input } from "#app/components/ui/forms/conform-integrated/input.tsx"
import { StatusButton } from "#app/components/ui/status-button.tsx"
import { APP_NAME } from "#app/utils/constants.ts"
import { prisma } from "#app/utils/db.server.ts"
import { sendEmail } from "#app/utils/email.server.ts"
import { checkHoneypot } from "#app/utils/honeypot.server.ts"
import { generateMeta } from "#app/utils/meta.js"
import { useIsPending } from "#app/utils/misc.tsx"
import { EmailSchema } from "#app/utils/user-validation.ts"
import routes from "#routes/index.js"
import { prepareVerification } from "./verify.server"

const ForgotPasswordSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					email: data.email,
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ["email"],
					code: z.ZodIssueCode.custom,
					message: "No hemos encontrado ningún usuario que coincida.",
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== "success") {
		return data(
			{
				result: submission.reply(),
			},
			{
				status: submission.status === "error" ? 400 : 200,
			},
		)
	}
	const { email } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: { email },
		select: { email: true },
	})

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: "reset-password",
		target: email,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `${APP_NAME} - Reestablecer Contraseña`,
		react: (
			<ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
		),
	})

	if (response.status === "success") {
		return redirect(redirectTo.toString())
	} else {
		return data(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

function ForgotPasswordEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="es" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>{APP_NAME} - Reestablecer Contraseña</E.Text>
				</h1>
				<p>
					<E.Text>
						Aquí tienes el código de verificación: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>O haz click en el enlace:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

export const meta = generateMeta({
	title: `Reestablecimiento de contraseña | ${APP_NAME}`,
	description: `Reestablece tu contraseña en ${APP_NAME}. Ingresa tu correo electrónico y te enviaremos las instrucciones de recuperación.`,
	canonical: routes.public.auth_forgot_password.route(),
})

export default function ForgotPasswordRoute() {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const [form, fields] = useForm({
		id: "forgot-password-form",
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: "onBlur",
	})

	return (
		<div className="container pb-32 pt-20">
			<div className="flex flex-col justify-center">
				<div className="text-center">
					<h1 className="text-h1">Reestablecer Contraseña</h1>
					<p className="mt-3 text-body-md text-muted-foreground">
						No te preocupes, te enviaremos las instrucciones.
					</p>
				</div>
				<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
					<FormContext context={form.context}>
						<Form method="POST" {...getFormProps(form)}>
							<HoneypotInputs />
							<div className="flex flex-col gap-4">
								<FormField field={fields.email}>
									<FormLabel>Correo electrónico</FormLabel>
									<FormControl>
										<Input autoFocus meta={fields.email} />
									</FormControl>
									<FormMessage />
								</FormField>

								<FormErrorList formId={form.id} />
							</div>

							<div className="mt-6">
								<StatusButton
									className="w-full"
									status={isPending ? "pending" : (form.status ?? "idle")}
									type="submit"
									disabled={isPending}
								>
									Recuperar contraseña
								</StatusButton>
							</div>
						</Form>
					</FormContext>
					<Link
						to="/login"
						className="mt-4 inline-block text-center text-body-sm font-bold"
					>
						Volver a inicio de sesión
					</Link>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
